import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../utils/Instance';
import { toast } from 'sonner';

/**
 * YotiAgeVerificationSimple Component
 * 
 * Simple age verification component that integrates with backend API
 * Uses Yoti Age Verification flow
 */
export default function YotiAgeVerificationSimple({ onCompletion, showModal = false, onClose }) {
  const { profile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState({
    verified: false,
    skipped: false,
    age: null,
    is_over_18: false,
    date_of_birth: null,
  });
  const [error, setError] = useState(null);

  const fetchVerificationStatus = useCallback(async () => {
    try {
      if (!profile || !profile.accessToken) {
        return;
      }

      const requirementResponse = await fetch(`${API_BASE}accounts/verify-age/requirement/`, {
        headers: {
          'Authorization': `Bearer ${profile.accessToken}`,
        },
      });

      if (requirementResponse.ok) {
        const requirementData = await requirementResponse.json();
        if (requirementData.verification_required === false) {
          setVerificationStatus({
            verified: true,
            skipped: true,
            age: null,
            is_over_18: true,
            date_of_birth: null,
          });
          return;
        }
      }

      const response = await fetch(`${API_BASE}accounts/verify-age/latest/`, {
        headers: {
          'Authorization': `Bearer ${profile.accessToken}`,
        },
      });

      if (response.ok) {
        // Check if response is JSON
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          console.error('Non-JSON response received from latest endpoint');
          return;
        }

        const data = await response.json();
        if (data.is_verified) {
          setVerificationStatus({
            verified: true,
            skipped: false,
            age: data.age,
            is_over_18: data.is_over_18,
            date_of_birth: data.date_of_birth,
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch verification status:', error);
    }
  }, [profile]);

  // Fetch latest verification status on mount
  useEffect(() => {
    if (profile?.accessToken) {
      fetchVerificationStatus();
    }
  }, [profile?.accessToken, fetchVerificationStatus]);

  const startYotiVerification = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!profile || !profile.accessToken) {
        toast.error('Please log in to verify your age');
        return;
      }

      // Step 1: Create session with backend
      const sessionResponse = await fetch(`${API_BASE}accounts/verify-age/create-session/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${profile.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          // callback_url will be set by backend if not provided
          // Backend will use its own URL for the callback
        }),
      });

      // Check if response is JSON
      const contentType = sessionResponse.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await sessionResponse.text();
        console.error('Non-JSON response received:', text.substring(0, 200));
        throw new Error(`Server returned HTML instead of JSON. Status: ${sessionResponse.status}. Check server logs.`);
      }

      const sessionData = await sessionResponse.json();

      if (sessionData.verification_skipped) {
        setVerificationStatus({
          verified: true,
          skipped: true,
          age: null,
          is_over_18: true,
          date_of_birth: null,
        });
        toast.success('Age verification is not required for your current state.');
        if (onCompletion) {
          onCompletion(sessionData);
        }
        return;
      }

      if (!sessionResponse.ok || !sessionData.success) {
        throw new Error(sessionData.error || 'Failed to create verification session');
      }

      const { session_id, verification_url } = sessionData;

      if (!verification_url) {
        throw new Error('No verification URL received');
      }

      // Step 2: Store session_id for later polling
      localStorage.setItem('yoti_session_id', session_id);

      // Step 3: Open Yoti verification page
      const popup = window.open(
        verification_url,
        'YotiAgeVerification',
        'width=800,height=900,resizable=yes,scrollbars=yes'
      );

      if (!popup) {
        toast.error('Popup blocked. Please allow popups for this site.');
        return;
      }

      toast.success('Yoti verification opened. Please complete the verification.');

      // Step 4: Poll for session result
      const pollInterval = setInterval(async () => {
        try {
          const resultResponse = await fetch(`${API_BASE}accounts/verify-age/session/${session_id}/result/`, {
            headers: {
              'Authorization': `Bearer ${profile.accessToken}`,
            },
          });

          if (resultResponse.ok) {
            // Check if response is JSON
            const contentType = resultResponse.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
              const text = await resultResponse.text();
              console.error('Non-JSON response received:', text.substring(0, 200));
              return; // Skip this poll iteration
            }

            const resultData = await resultResponse.json();

            if (resultData.status === 'COMPLETE') {
              clearInterval(pollInterval);
              
              if (popup && !popup.closed) {
                popup.close();
              }

              // Update verification status
              setVerificationStatus({
                verified: true,
                skipped: false,
                age: resultData.age,
                is_over_18: resultData.is_over_18,
                date_of_birth: resultData.date_of_birth,
              });

              if (resultData.is_over_18) {
                toast.success(`Age verified successfully! You are ${resultData.age} years old.`);
              } else {
                toast.error('You must be 18 or older to use this service.');
              }

              if (onCompletion) {
                onCompletion(resultData);
              }

              localStorage.removeItem('yoti_session_id');
            } else if (resultData.status === 'FAIL' || resultData.status === 'ERROR' || resultData.status === 'CANCELLED') {
              clearInterval(pollInterval);
              if (popup && !popup.closed) {
                popup.close();
              }
              toast.error('Verification failed. Please try again.');
              setError(resultData.error || 'Verification failed');
              localStorage.removeItem('yoti_session_id');
            }
          }
        } catch (error) {
          console.error('Polling error:', error);
        }
      }, 3000); // Poll every 3 seconds

      // Stop polling after 10 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        if (popup && !popup.closed) {
          popup.close();
        }
      }, 600000);

      // Also check if popup is closed
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          // Continue polling for a bit after popup closes (user might have completed it)
          setTimeout(() => {
            clearInterval(pollInterval);
          }, 30000); // Poll for 30 more seconds after popup closes
        }
      }, 1000);

    } catch (error) {
      console.error('Verification error:', error);
      const errorMsg = error.message || 'Failed to start verification';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Modal version
  if (showModal) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
        <div className="bg-[#1c1c1e] rounded-lg shadow-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold text-[#CEB46A]">Age Verification (Yoti)</h3>
            {onClose && (
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          <div className="space-y-4">
            {verificationStatus.verified ? (
              <div className="p-4 bg-green-900 bg-opacity-30 border border-green-700 rounded">
                <div className="flex items-center gap-2 text-green-400">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="font-semibold">Age Verified</span>
                </div>
                <div className="mt-2 text-gray-300">
                  {verificationStatus.skipped ? (
                    <p>Yoti verification is not required for this account state.</p>
                  ) : (
                    <>
                      <p>Age: {verificationStatus.age} years old</p>
                      <p>Status: {verificationStatus.is_over_18 ? 'Over 18 ✓' : 'Under 18 ✗'}</p>
                      {verificationStatus.date_of_birth && (
                        <p className="text-sm text-gray-400">
                          Date of Birth: {new Date(verificationStatus.date_of_birth).toLocaleDateString()}
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
            ) : (
              <>
                <div className="p-4 bg-blue-900 bg-opacity-30 border border-blue-700 rounded">
                  <p className="text-blue-200 text-sm mb-2">
                    <strong>Age Verification Required</strong>
                  </p>
                  <p className="text-gray-300 text-sm">
                    Verify your age using Yoti to ensure you meet age requirements (18+).
                  </p>
                </div>

                {error && (
                  <div className="p-4 bg-red-900 bg-opacity-30 border border-red-700 rounded">
                    <p className="text-red-200 text-sm">{error}</p>
                  </div>
                )}

                <button
                  onClick={startYotiVerification}
                  disabled={loading}
                  className="w-full py-3 px-4 bg-[#CEB46A] text-black font-medium rounded hover:bg-[#b9a05a] transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Verifying...' : 'Start Yoti Verification'}
                </button>

                <p className="text-gray-400 text-xs text-center">
                  Click the button above to open Yoti Age Verification in a popup window.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Inline version
  return (
    <div className="bg-[#1C1C1E] rounded-lg p-6">
      <h3 className="text-lg font-semibold text-[#CEB46A] mb-4">
        Age Verification (Yoti)
      </h3>

      {verificationStatus.verified ? (
        <div className="p-4 bg-green-900 bg-opacity-30 border border-green-700 rounded">
          <div className="flex items-center gap-2 text-green-400">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="font-semibold">Age Verified</span>
          </div>
          <div className="mt-2 text-gray-300">
            {verificationStatus.skipped ? (
              <p>Yoti verification is not required for this account state.</p>
            ) : (
              <>
                <p>Age: {verificationStatus.age} years old</p>
                <p>Status: {verificationStatus.is_over_18 ? 'Over 18 ✓' : 'Under 18 ✗'}</p>
                {verificationStatus.date_of_birth && (
                  <p className="text-sm text-gray-400 mt-1">
                    Verified on: {new Date(verificationStatus.date_of_birth).toLocaleDateString()}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      ) : (
        <>
          <p className="text-gray-300 text-sm mb-4">
            Verify your age using Yoti to ensure you meet age requirements (18+).
          </p>

          {error && (
            <div className="p-3 bg-red-900 bg-opacity-30 border border-red-700 rounded mb-4">
              <p className="text-red-200 text-sm">{error}</p>
            </div>
          )}

          <button
            onClick={startYotiVerification}
            disabled={loading || !profile || !profile.accessToken}
            className="w-full py-3 px-4 bg-[#CEB46A] text-black font-medium rounded hover:bg-[#b9a05a] transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Verifying...' : 'Start Yoti Verification'}
          </button>
        </>
      )}
    </div>
  );
}
